/* ============================================================
   keys.js — every key this site keeps in the browser, in one table (PLAN-2026-09-23-layers §3.5).

   Two layers own keys, and the direction never reverses: core writes its own (the login, the mock's fake account, the
   harness session pointer) through token.js / real.js / mock.js / agent.js; the ROOM's keys are written through
   js/room/memory.js only. Both import the names from here, so a rename is one edit and the inline scripts in
   index.html / listen.html (which cannot import) are checked against this file by scripts/room.test.mjs.

   `store`: local = localStorage, session = sessionStorage, cookie = document.cookie.
   Keys with a parameter are functions of it.
   ============================================================ */
export const K = {
  /* ---- core: the login (js/core/token.js) ---- */
  ACCESS: 'miya_access',
  REFRESH: 'miya_refresh',
  /* ---- core: the OAuth round trip (js/core/real.js, js/core/mock.js, the two callback pages) ---- */
  X_PEND: 'miya_x_pend' /* cookie (a localStorage copy rides beside it, best effort) */,
  X_RESULT: 'miya_x_result' /* sessionStorage: written by the callback page, read once by finishLogin */,
  /* ---- core: the mock station's account (js/core/mock.js) ---- */
  MOCK_USER: 'miya_mock_user',
  MOCK_INVITE: 'miya_mock_invite',
  MOCK_INVITES: 'miya_mock_invites',
  MOCK_SEEN: 'miya_mock_seen',
  /* ---- core: the harness (js/core/agent.js) ---- */
  SESSION: 'miya.listen.session.miya' /* the pointer to this browser's session with the one persona */,
  FRESH: 'miya.fresh' /* sessionStorage: ?fresh asked for a room with no session — read once by agent.js */,
  mockTurns: (persona) => `miya.listen.mock-turns.${persona}` /* the mock agent's record */,

  /* ---- room (js/room/memory.js) ---- */
  ROOM: 'miya.listen.v1' /* the queue as metadata, which record, the theme, the source, the prompt */,
  ROOM_POS: 'miya.listen.pos.v1' /* the hot cursor alone */,
  VIBE: 'miya.listen.vibe.v1',
  VIBE_POS: 'miya.listen.vibe.pos.v1',
  SONGS: 'miya.listen.songs.v1' /* the thin song cache */,
  VOLUME: 'miya.listen.volume',
  SHIM: 'miya.listen.shim' /* which queue_updates went into which playlist */,
  PHOTO_HINT: 'miya.photoHintDone',
  thread: (persona) => `miya.listen.thread.${persona}` /* the last thread drawn, for the memory-first paint */,
  seen: (sub) => `miya.listen.seen.${sub}` /* a pass-link account's first night, remembered */,
  profileSent: (uid) => `miya.listen.profileSent.${uid}`,
  skipped: (uid) => `miya.listen.skipped.${uid}`,
  repeat: (uid, kind) => `miya.listen.repeat.${uid}.${kind}` /* kind: vibe | list */,
  place: (uid) => `miya.listen.place.${uid}`,
  stopped: (uid) => `miya.listen.stopped.${uid}` /* the turns you stopped with ■ (the record has no word for it) */,
  playlists: (uid) => `miya.playlists.${uid || 'anon'}`,
  playlistsOrder: (uid) => `miya.playlists.order.${uid || 'anon'}`,
  playbookPressed: (uid) => `miya.playbook.pressed.${uid}`,
  playbookCard: (uid) => `miya.playbook.card.${uid}`,
};

/* the room's keys, by prefix — what sign-out and ?fresh sweep (memory.forget) */
export const ROOM_PREFIXES = ['miya.listen.', 'miya.playlists.', 'miya.playbook.', 'miya.photoHintDone'];

/* every literal a key can take, for the tests that read the inline scripts: the fixed names, and the prefixes of the
   parametrised ones */
export const ALL_LITERALS = [
  ...Object.values(K).filter((v) => typeof v === 'string'),
  'miya.listen.thread.',
  'miya.listen.mock-turns.',
  'miya.listen.seen.',
  'miya.listen.profileSent.',
  'miya.listen.skipped.',
  'miya.listen.repeat.',
  'miya.listen.place.',
  'miya.listen.stopped.',
  'miya.playlists.',
  'miya.playlists.order.',
  'miya.playbook.pressed.',
  'miya.playbook.card.',
];
